import { Menu, MenuItem, MenuItemProps, MenuProps } from '@material-ui/core';
import React, { forwardRef, useCallback, useMemo, useRef } from 'react';
import { useOpen } from 'AurionCR/components/hooks';
import { useMergeRef } from 'hooks/use-merge-ref';

export interface MenuButtonItem
  extends Partial<Omit<MenuItemProps<any, any>, 'title' | 'onClick'>> {
  title: string | React.ReactNode;
  onClick?: () => void;
}

export interface MenuButtonProps {
  items: MenuButtonItem[];
  isOpen?: boolean;
  onClose?: () => void;
  onOpen?: (e: React.MouseEvent) => void;
  MenuProps?: Partial<MenuProps>;
  closeOnClick?: boolean;
  children: React.ReactNode;
}

export const MenuButton = forwardRef<any, MenuButtonProps>(
  (
    {
      items,
      isOpen: _isOpen,
      onClose: _onClose,
      onOpen: _onOpen,
      MenuProps,
      closeOnClick = true,
      children,
    },
    outerRef,
  ) => {
    const { isOpen: innerIsOpen, handleOpen: innerOnOpen, handleClose: innerOnClose } = useOpen();
    const isOpen = _isOpen === undefined ? innerIsOpen : _isOpen;
    const onClose = _onClose === undefined ? innerOnClose : _onClose;
    const onOpen = _onOpen === undefined ? innerOnOpen : _onOpen;

    const ref = useRef<any>();
    const mergedRef = useMergeRef(ref, outerRef);
    const factoryClick = useCallback(
      (handle: MenuButtonItem['onClick']) => {
        return () => {
          handle && handle();
          if (closeOnClick) {
            onClose();
          }
        };
      },
      [closeOnClick, onClose],
    );
    const Child = useMemo(() => {
      return React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // @ts-ignore
          return React.cloneElement(child, { ref: mergedRef, onClick: onOpen, ...child.props });
        }
        return child;
      });
    }, [children, mergedRef, onOpen]);

    return (
      <>
        {Child}
        <Menu
          anchorEl={ref.current}
          open={isOpen}
          onClose={onClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          {...MenuProps}
        >
          {items.map(({ title, onClick, ...rest }, i) => (
            <MenuItem key={i} {...rest} onClick={factoryClick(onClick)}>
              {title}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  },
);
